import { Box } from '@mui/material';
import type { ReactNode } from 'react';
import { useSilobagListContext } from '../../context';
import type { SilobagTab, SilosProTab } from '../../hook/use-silobag-list-logic-state';
import { TABLE_COUNTER } from './table-counter-group';

type CounterByState = Record<'silospro' | 'silobag', Record<SilosProTab | SilobagTab, ReactNode>>;

const LOADER_CELLS = Array.from({ length: 9 }, (_, i) => i);
const LOADER_CELL_SIZE = 6;
const LOADER_CELL_GAP = 4;
const LOADER_GRID_SIZE = LOADER_CELL_SIZE * 3 + LOADER_CELL_GAP * 2;
const SILOREAL_LOADER_GRADIENT = 'linear-gradient(132deg,rgb(123, 178, 28) 14%,rgb(120, 189, 228) 66%)';

function loaderCellBackgroundPosition(cell: number) {
  const col = cell % 3;
  const row = Math.floor(cell / 3);
  const step = LOADER_CELL_SIZE + LOADER_CELL_GAP;

  return `-${col * step}px -${row * step}px`;
}

const SILOREAL_GREEN = [123, 178, 28] as const;
const SILOREAL_CYAN = [120, 189, 228] as const;

function sampleSiloRealGradient(t: number) {
  const mix = t <= 0.14 ? 0 : t >= 0.66 ? 1 : (t - 0.14) / (0.66 - 0.14);
  const r = Math.round(SILOREAL_GREEN[0] + (SILOREAL_CYAN[0] - SILOREAL_GREEN[0]) * mix);
  const g = Math.round(SILOREAL_GREEN[1] + (SILOREAL_CYAN[1] - SILOREAL_GREEN[1]) * mix);
  const b = Math.round(SILOREAL_GREEN[2] + (SILOREAL_CYAN[2] - SILOREAL_GREEN[2]) * mix);

  return `rgb(${r}, ${g}, ${b})`;
}

function loaderCellBoxShadow(cell: number) {
  const col = cell % 3;
  const step = LOADER_CELL_SIZE + LOADER_CELL_GAP;
  const left = (col * step) / LOADER_GRID_SIZE;
  const right = (col * step + LOADER_CELL_SIZE) / LOADER_GRID_SIZE;

  return `-0.5px -0.5px 1px ${sampleSiloRealGradient(left)}, 0.5px 0.5px 1px ${sampleSiloRealGradient(right)}`;
}

function CounterLoadingOverlay() {
  return (
    <Box
      role="status"
      aria-live="polite"
      aria-label="Actualizando contadores"
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        display: 'grid',
        placeItems: 'center',
        borderRadius: 2.5,
        backdropFilter: 'blur(1.5px)',
        pointerEvents: 'none',
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(3, ${LOADER_CELL_SIZE}px)`,
          gap: `${LOADER_CELL_GAP}px`,
          '@keyframes counterCellPulse': {
            '0%, 100%': {
              opacity: 0.22,
              transform: 'scale(0.72)',
            },
            '45%': {
              opacity: 1,
              transform: 'scale(1)',
            },
          },
        }}
      >
        {LOADER_CELLS.map((cell) => (
          <Box
            key={cell}
            sx={{
              width: LOADER_CELL_SIZE,
              height: LOADER_CELL_SIZE,
              borderRadius: '1.5px',
              backgroundImage: SILOREAL_LOADER_GRADIENT,
              backgroundSize: `${LOADER_GRID_SIZE}px ${LOADER_GRID_SIZE}px`,
              backgroundPosition: loaderCellBackgroundPosition(cell),
              backgroundRepeat: 'no-repeat',
              boxShadow: loaderCellBoxShadow(cell),
              animation: 'counterCellPulse 0.85s ease-in-out infinite',
              animationDelay: `${cell * 80}ms`,
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                opacity: 0.5,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

export function Counter() {
  const {
    state: { current },
    async: {
      summary: {
        loading: { current: isSummaryLoading },
      },
    },
  } = useSilobagListContext();

  const isArchived = current.view === 'silobag' && current.tab === 'archived';
  const showSummaryLoading = isSummaryLoading && !isArchived;

  const COUNTER_BY_STATE = {
    silospro: {
      vrr: <TABLE_COUNTER.SILOS_PRO.VRR />,
      'no-vrr': <TABLE_COUNTER.SILOS_PRO.WITHOUT_VRR />,
      closed: <TABLE_COUNTER.SILOS_PRO.CLOSED />,
    },
    silobag: {
      active: <TABLE_COUNTER.SILOBAG.ACTIVE />,
      closed: <TABLE_COUNTER.SILOBAG.CLOSED />,
      archived: <TABLE_COUNTER.SILOBAG.ARCHIVED />,
    },
  } as CounterByState;

  const isFullWidthCounter = current.view === 'silobag' && current.tab === 'active';

  return (
    <Box
      position="relative"
      width={isFullWidthCounter ? '100%' : 'fit-content'}
      maxWidth="100%"
      aria-busy={showSummaryLoading}
    >
      <Box
        sx={{
          opacity: showSummaryLoading ? 0.42 : 1,
          filter: showSummaryLoading ? 'saturate(0.55)' : 'none',
          transition: 'opacity 180ms ease, filter 180ms ease',
        }}
      >
        {COUNTER_BY_STATE[current.view][current.tab] ?? null}
      </Box>
      {showSummaryLoading ? <CounterLoadingOverlay /> : null}
    </Box>
  );
}
